import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PhoneCall,
  ExternalLink,
  CalendarClock,
  CalendarDays,
  PhoneForwarded,
  ListTodo,
  Sparkles,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { canProgressUpdateContact } from "./contactHistoryAccess";
import ContactFilter, { getDefaultContactFilters } from "./ContactFilter";
import Pagination from "../../../components/common/Pagination";
import CreateModal from "./ContactHistoryCreateModal";
import DetailModal from "./ContactHistoryDetailModal";
import {
  getAllContactHistories,
  getContactHistoryById,
  getContactHistoryStats,
  getContactHistoriesByCustomer,
} from "../../../api/crm/contact-history/info";
import { showErrorToast } from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import { format } from "date-fns";
import { startContactHistoryPageTour } from "./contactHistoryPageTour";

const TAB_ORDER = { followup: 0, all: 1 };

const tabPanelVariants = {
  enter: (dir) => ({
    opacity: 0,
    x: dir > 0 ? 28 : -28,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir > 0 ? -20 : 20,
    transition: { duration: 0.18, ease: "easeIn" },
  }),
};

const cardListVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.045, delayChildren: 0.04 },
  },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
  },
};

/** Dòng phụ: chi nhánh + địa lý khách */
function formatBranchAndGeoLine(item) {
  const branch = (item.branch_name || "").trim();
  const district = (item.customer_district ?? item.district ?? "").trim();
  const ward = (item.customer_ward ?? item.ward ?? "").trim();
  const geo = [district, ward].filter(Boolean).join(" • ");
  if (branch && geo) return `${branch} • ${geo}`;
  if (branch) return branch;
  if (geo) return geo;
  return "—";
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Phân loại hạn follow-up để sale ưu tiên đúng việc */
function getFollowupMeta(dateStr) {
  if (!dateStr) return null;
  const target = startOfDay(dateStr);
  const today = startOfDay(new Date());
  const diff = Math.round((target - today) / 86400000);
  if (diff < 0) {
    return {
      short: `Quá hạn ${Math.abs(diff)}n`,
      label: `Quá hạn ${Math.abs(diff)} ngày`,
      tone: "overdue",
      className: "bg-red-100 text-red-800 border-red-200",
    };
  }
  if (diff === 0) {
    return {
      short: "Hôm nay",
      label: "Hẹn hôm nay",
      tone: "today",
      className: "bg-amber-100 text-amber-900 border-amber-200",
    };
  }
  if (diff === 1) {
    return {
      short: "Ngày mai",
      label: "Hẹn ngày mai",
      tone: "soon",
      className: "bg-orange-50 text-orange-800 border-orange-100",
    };
  }
  return {
    short: format(target, "dd/MM"),
    label: `Hẹn ${format(target, "dd/MM/yyyy")}`,
    tone: "ok",
    className: "bg-slate-100 text-slate-700 border-slate-200",
  };
}

const ContactHistoryListPage = () => {
  const canCreate = hasPermission("crm.contact_history", "C");
  const canProductTour = hasPermission("crm.contact_history", "R");

  const canProgressItem = (item) =>
    canProgressUpdateContact(item, { hasCreatePermission: canCreate });

  const statCards = [
    {
      key: "total_contacts",
      label: "Tổng liên hệ",
      shortLabel: "Tổng",
      icon: ListTodo,
      valueClass: "text-slate-900",
      labelClass: "text-slate-700",
      iconWrapClass: "border border-slate-300 bg-white text-slate-800 shadow-sm",
      cardClass:
        "border-slate-300 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-50 shadow-slate-200/70",
      blobClass: "bg-slate-400/55",
      watermarkClass: "text-slate-500/35",
      activeRing: "border-slate-400 ring-2 ring-slate-300/80",
    },
    {
      key: "due_today_followups",
      label: "Cần liên hệ hôm nay",
      shortLabel: "Cần LH",
      icon: CalendarDays,
      valueClass: "text-sky-900",
      labelClass: "text-sky-800",
      iconWrapClass: "border border-sky-300 bg-white text-sky-800 shadow-sm",
      cardClass:
        "border-sky-300 bg-gradient-to-br from-sky-200 via-sky-100 to-blue-50 shadow-sky-200/60",
      blobClass: "bg-sky-400/55",
      watermarkClass: "text-sky-600/40",
      activeRing: "border-sky-400 ring-2 ring-sky-300/80",
    },
    {
      key: "upcoming_followups",
      label: "Có lịch hẹn",
      shortLabel: "Hẹn lại",
      icon: CalendarClock,
      valueClass: "text-amber-900",
      labelClass: "text-amber-800",
      iconWrapClass: "border border-amber-300 bg-white text-amber-800 shadow-sm",
      cardClass:
        "border-amber-300 bg-gradient-to-br from-amber-200 via-amber-100 to-orange-50 shadow-amber-200/60",
      blobClass: "bg-amber-400/55",
      watermarkClass: "text-amber-600/40",
      activeRing: "border-amber-400 ring-2 ring-amber-300/80",
    },
    {
      key: "phone_contacts",
      label: "Gọi điện",
      shortLabel: "Gọi",
      icon: PhoneForwarded,
      valueClass: "text-emerald-900",
      labelClass: "text-emerald-800",
      iconWrapClass:
        "border border-emerald-300 bg-white text-emerald-800 shadow-sm",
      cardClass:
        "border-emerald-300 bg-gradient-to-br from-emerald-200 via-emerald-100 to-teal-50 shadow-emerald-200/60",
      blobClass: "bg-emerald-400/55",
      watermarkClass: "text-emerald-600/40",
      activeRing: "border-emerald-400 ring-2 ring-emerald-300/80",
    },
  ];

  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({
    total_contacts: 0,
    due_today_followups: 0,
    actionable_followups: 0,
    upcoming_followups: 0,
    phone_contacts: 0,
  });
  const [actionableReminders, setActionableReminders] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(() => getDefaultContactFilters());
  /** Mobile: ưu tiên luồng follow-up cho sale */
  const [mobileTab, setMobileTab] = useState("followup");
  const [tabDirection, setTabDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  /** Desktop/mobile: card KPI đang chọn (lọc nhanh danh sách) */
  const [statFilter, setStatFilter] = useState(null); // null | "due_today" | "followup" | "phone"
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [createMode, setCreateMode] = useState("create");
  const [relatedLogs, setRelatedLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "contact_date",
    direction: "desc",
  });
  const [columnWidths, setColumnWidths] = useState({
    contact_date: 140,
    customer_branch: 360,
    staff_name: 180,
    method: 150,
    content_result: 480,
  });
  const minColumnWidths = {
    contact_date: 130,
    customer_branch: 260,
    staff_name: 150,
    method: 130,
    content_result: 280,
  };
  const [activeResize, setActiveResize] = useState(null);

  const handleProductTour = useCallback(() => {
    window.setTimeout(() => startContactHistoryPageTour(), 80);
  }, []);

  useEffect(() => {
    const sync = () => setIsMobile(window.innerWidth < 768);
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  const queryFilters = useMemo(() => {
    const base = { ...filters };

    // Card KPI (ưu tiên hơn advanced filter tương ứng)
    if (statFilter === "due_today") {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      base.next_contact_on = `${y}-${m}-${d}`;
      delete base.month;
      delete base.contact_date;
    } else if (statFilter === "phone") {
      base.contact_method = "Gọi điện";
    } else if (statFilter === "followup") {
      base.has_followup = "1";
    }

    // Tab mobile “Cần gọi lại” luôn ưu tiên
    if (isMobile && mobileTab === "followup") {
      base.has_followup = "1";
    }

    Object.keys(base).forEach((key) => {
      if (base[key] === "" || base[key] == null) delete base[key];
    });
    return base;
  }, [filters, mobileTab, isMobile, statFilter]);

  const followupFilterActive =
    statFilter === "followup" || (isMobile && mobileTab === "followup");

  const reminderScopeFilters = useMemo(() => {
    const base = { ...filters };
    delete base.month;
    delete base.contact_date;
    delete base.next_contact_on;
    delete base.has_followup;
    Object.keys(base).forEach((key) => {
      if (base[key] === "" || base[key] == null) delete base[key];
    });
    return base;
  }, [filters]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {
        ...queryFilters,
        page: currentPage,
        pageSize,
      };
      const [data, statData, actionableData] = await Promise.all([
        getAllContactHistories(params),
        getContactHistoryStats({ ...filters }),
        getAllContactHistories({
          ...reminderScopeFilters,
          followup_due: "1",
          page: 1,
          pageSize: 50,
        }),
      ]);
      setContacts(data.items || []);
      setTotal(data.total || 0);
      setActionableReminders(
        Array.isArray(actionableData?.items) ? actionableData.items : []
      );
      setStats({
        total_contacts: Number(statData?.total_contacts || 0),
        due_today_followups: Number(
          statData?.due_today_followups ??
            statData?.today_contacts ??
            0
        ),
        actionable_followups: Number(
          statData?.actionable_followups ??
            statData?.due_today_followups ??
            0
        ),
        upcoming_followups: Number(statData?.upcoming_followups || 0),
        phone_contacts: Number(statData?.phone_contacts || 0),
      });
    } catch (err) {
      console.error("Lỗi khi lấy danh sách liên hệ:", err);
      showErrorToast("Không thể tải danh sách liên hệ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [queryFilters, currentPage, pageSize, reminderScopeFilters]);

  useEffect(() => {
    if (!activeResize) return undefined;
    const handleMove = (e) => {
      const delta = e.clientX - activeResize.startX;
      setColumnWidths((prev) => ({
        ...prev,
        [activeResize.key]: Math.max(
          minColumnWidths[activeResize.key] || 110,
          activeResize.startWidth + delta
        ),
      }));
    };
    const handleUp = () => setActiveResize(null);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [activeResize]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
  };

  const sortedContacts = useMemo(() => {
    const list = [...contacts];
    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (key === "contact_date" || key === "next_contact_date") {
        const av = a[key] ? new Date(a[key]).getTime() : 0;
        const bv = b[key] ? new Date(b[key]).getTime() : 0;
        return (av - bv) * dir;
      }
      const av = String(a[key] || "").toLowerCase();
      const bv = String(b[key] || "").toLowerCase();
      return av.localeCompare(bv, "vi") * dir;
    });
    return list;
  }, [contacts, sortConfig]);

  const reminderContacts = useMemo(() => {
    // Khi đang “Xem tất cả”: hiện mọi lịch hẹn trong trang kết quả lọc
    if (followupFilterActive) {
      return sortedContacts
        .filter((item) => !!item.next_contact_date)
        .sort(
          (a, b) =>
            new Date(a.next_contact_date).getTime() -
            new Date(b.next_contact_date).getTime()
        );
    }
    // Mặc định: chỉ hôm nay + quá hạn (fetch riêng, không phụ thuộc tháng)
    return [...actionableReminders].sort(
      (a, b) =>
        new Date(a.next_contact_date).getTime() -
        new Date(b.next_contact_date).getTime()
    );
  }, [sortedContacts, followupFilterActive, actionableReminders]);
  const completedContacts = useMemo(
    () => sortedContacts.filter((item) => !item.next_contact_date),
    [sortedContacts]
  );

  const sortMark = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const openCreate = () => {
    setEditingContact(null);
    setCreateMode("create");
    setShowCreateModal(true);
  };

  const handleView = async (id) => {
    try {
      const data = await getContactHistoryById(id);
      const logsRes = await getContactHistoriesByCustomer(data.customer_id, {
        limit: 20,
      });
      setRelatedLogs(logsRes?.items || []);
      setSelectedContact(data);
      setShowDetailModal(true);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải chi tiết liên hệ");
    }
  };

  const handleProgress = async (id, contactHint = null) => {
    if (contactHint && !canProgressItem(contactHint)) {
      showErrorToast(
        "Chỉ người tạo hoặc nhân sự cùng chi nhánh mới được cập nhật tiến độ"
      );
      return;
    }
    try {
      const data = await getContactHistoryById(id);
      if (!canProgressItem(data)) {
        showErrorToast(
          "Chỉ người tạo hoặc nhân sự cùng chi nhánh mới được cập nhật tiến độ"
        );
        return;
      }
      setEditingContact(data);
      setCreateMode("progress");
      setShowDetailModal(false);
      setShowCreateModal(true);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải dữ liệu để cập nhật tiến độ");
    }
  };

  const handleMobileTabChange = useCallback((tab) => {
    setTabDirection((TAB_ORDER[tab] ?? 0) >= (TAB_ORDER[mobileTab] ?? 0) ? 1 : -1);
    setMobileTab(tab);
    // Đồng bộ card KPI với tab mobile
    if (tab === "followup") setStatFilter("followup");
    else if (statFilter === "followup") setStatFilter(null);
    setCurrentPage(1);
  }, [mobileTab, statFilter]);

  const handleStatCardClick = (cardKey) => {
    if (cardKey === "total_contacts") {
      setStatFilter(null);
      if (isMobile) {
        setTabDirection((TAB_ORDER.all ?? 0) >= (TAB_ORDER[mobileTab] ?? 0) ? 1 : -1);
        setMobileTab("all");
      }
      setCurrentPage(1);
      return;
    }

    const next =
      cardKey === "due_today_followups"
        ? "due_today"
        : cardKey === "upcoming_followups"
          ? "followup"
          : cardKey === "phone_contacts"
            ? "phone"
            : null;

    if (!next) return;

    const turningOff = statFilter === next;
    setStatFilter(turningOff ? null : next);

    if (isMobile) {
      const targetTab = !turningOff && next === "followup" ? "followup" : "all";
      setTabDirection(
        (TAB_ORDER[targetTab] ?? 0) >= (TAB_ORDER[mobileTab] ?? 0) ? 1 : -1
      );
      setMobileTab(targetTab);
    }

    setCurrentPage(1);
  };

  const renderMobileContactCard = (item, { emphasizeFollowup = false } = {}) => {
    const meta = getFollowupMeta(item.next_contact_date);
    const initial = (item.customer_name || "?").charAt(0).toUpperCase();
    const toneBorder =
      meta?.tone === "overdue"
        ? "border-red-200"
        : meta?.tone === "today"
        ? "border-amber-200"
        : meta?.tone === "soon"
        ? "border-orange-200"
        : "border-gray-100";
    const toneBg =
      meta?.tone === "overdue"
        ? "bg-gradient-to-br from-red-50 via-white to-rose-50/80"
        : meta?.tone === "today"
        ? "bg-gradient-to-br from-amber-50 via-white to-orange-50/70"
        : meta?.tone === "soon"
        ? "bg-gradient-to-br from-orange-50 via-white to-amber-50/60"
        : emphasizeFollowup
        ? "bg-gradient-to-br from-slate-50 via-white to-rose-50/40"
        : "bg-gradient-to-br from-white via-white to-slate-50";

    const showProgress =
      (emphasizeFollowup || item.next_contact_date) && canProgressItem(item);

    return (
      <motion.article
        key={item.id}
        variants={cardItemVariants}
        layout={false}
        role="button"
        tabIndex={0}
        onClick={() => handleView(item.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleView(item.id);
          }
        }}
        className={`relative overflow-hidden rounded-2xl border p-3.5 shadow-sm active:scale-[0.99] ${toneBorder} ${toneBg}`}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-red-100/40 blur-2xl"
        />
        <div className="relative flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white bg-gradient-to-br from-red-600 to-rose-700 text-sm font-black text-white shadow-md shadow-red-100">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-black text-gray-900">
                  {item.customer_name || "—"}
                </p>
                <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                  {formatBranchAndGeoLine(item)}
                </p>
              </div>
              {meta ? (
                <span
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.className}`}
                >
                  {meta.short}
                </span>
              ) : null}
            </div>

            <p className="mt-2 line-clamp-2 text-[13px] leading-snug text-gray-700">
              {item.content || "—"}
            </p>

            <div className="mt-2.5 grid grid-cols-2 gap-x-2 gap-y-1.5 text-[11px]">
              <div className="flex min-w-0 items-center gap-1.5 text-gray-600">
                <CalendarDays className="h-3.5 w-3.5 shrink-0 text-red-500" />
                <span className="truncate font-semibold text-gray-800">
                  {item.contact_date
                    ? format(new Date(item.contact_date), "dd/MM/yyyy")
                    : "—"}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="inline-flex max-w-full truncate rounded-full border border-purple-100 bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-800">
                  {item.method || "—"}
                </span>
              </div>
              <div className="col-span-2 flex min-w-0 items-center gap-1.5 text-gray-500">
                <span className="truncate text-[11px]">
                  NV:{" "}
                  <span className="font-semibold text-gray-700">
                    {item.staff_name || "—"}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {showProgress ? (
          <div className="relative mt-3 border-t border-white/70 pt-3">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleProgress(item.id, item);
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-red-700 to-rose-700 px-3 py-2.5 text-xs font-bold text-white shadow-sm shadow-red-100 active:scale-[0.98]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Cập nhật tiến độ
            </button>
          </div>
        ) : null}
      </motion.article>
    );
  };

  return (
    <div className="p-4 pb-24 md:pb-4">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="contact-history-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <PhoneCall className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Lịch sử <span className="text-red-700">Liên hệ</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Cập nhật tương tác khách hàng ngay khi phát sinh
              </p>
            </div>
          </div>
        </div>
        {canProductTour ? (
          <button
            type="button"
            onClick={handleProductTour}
            className="hidden shrink-0 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50/90 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100 sm:inline-flex"
          >
            <Sparkles className="h-4 w-4 text-red-600" strokeWidth={2.25} />
            Hướng dẫn sử dụng
          </button>
        ) : null}
      </div>

      {/* Mini stats */}
      <div className="mb-4 md:mb-6" data-tour="contact-stats">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-2 md:gap-4 xl:grid-cols-4">
          {statCards.map((card) => {
            const Icon = card.icon;
            const value = stats?.[card.key] ?? 0;
            const isActive =
              (card.key === "total_contacts" &&
                !statFilter &&
                !(isMobile && mobileTab === "followup")) ||
              (card.key === "due_today_followups" &&
                statFilter === "due_today") ||
              (card.key === "upcoming_followups" && followupFilterActive) ||
              (card.key === "phone_contacts" && statFilter === "phone");
            const activeRing = card.activeRing;
            return (
              <button
                key={card.key}
                type="button"
                title={`Lọc: ${card.label}`}
                onClick={() => handleStatCardClick(card.key)}
                className={`relative overflow-hidden rounded-2xl border p-2.5 text-left shadow-sm transition active:scale-[0.98] md:p-4 md:shadow-md ${
                  card.cardClass
                } ${isActive ? activeRing : ""}`}
              >
                <div
                  aria-hidden
                  className={`pointer-events-none absolute -right-3 -top-4 h-16 w-16 rounded-full blur-xl md:-right-8 md:-top-10 md:h-28 md:w-28 md:blur-2xl ${card.blobClass}`}
                />
                <Icon
                  aria-hidden
                  className={`pointer-events-none absolute -bottom-3 -right-2 hidden h-24 w-24 md:block ${card.watermarkClass}`}
                  strokeWidth={1.25}
                />
                <div className="relative z-10 flex items-center gap-2.5 md:hidden">
                  <div className={`rounded-xl p-2 ${card.iconWrapClass}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p
                      className={`text-xl font-black tabular-nums leading-none ${card.valueClass}`}
                    >
                      {value}
                    </p>
                    <p className="mt-1 truncate text-[10px] font-bold uppercase tracking-wide text-gray-500">
                      {card.shortLabel}
                    </p>
                  </div>
                </div>
                <div className="relative z-10 hidden md:block">
                  <div className="flex items-start justify-between gap-3">
                    <p
                      className={`text-[11px] font-bold uppercase tracking-wider ${card.labelClass}`}
                    >
                      {card.label}
                    </p>
                    <div className={`rounded-xl p-2 ${card.iconWrapClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                  </div>
                  <p className={`mt-3 text-3xl font-black tabular-nums ${card.valueClass}`}>
                    {value}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4 md:mb-6">
        <ContactFilter
          contactMethodOptions={[
            { value: "Gọi điện", label: "Gọi điện" },
            { value: "Email", label: "Email" },
            { value: "Gặp trực tiếp", label: "Gặp trực tiếp" },
            { value: "Zalo", label: "Zalo" },
          ]}
          followupCount={stats.upcoming_followups}
          mobileTab={mobileTab}
          onMobileTabChange={handleMobileTabChange}
          showMobileFab={!showCreateModal && !showDetailModal}
          onFilterChange={(newFilters) => {
            setFilters(newFilters);
            setCurrentPage(1);
          }}
          onRefresh={fetchContacts}
          onNewClick={openCreate}
        />
      </div>

      {/* ===== MOBILE: card feed ===== */}
      <div className="relative md:hidden" data-tour="contact-reminder-section">
        <AnimatePresence mode="wait" custom={tabDirection} initial={false}>
          <motion.div
            key={mobileTab}
            custom={tabDirection}
            variants={tabPanelVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-3"
          >
            {mobileTab === "followup" ? (
              <>
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <p className="text-xs font-bold">
                    Ưu tiên khách cần gọi lại ({total})
                  </p>
                </div>
                {loading ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
                    Đang tải…
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/60 px-4 py-6 text-center text-sm text-amber-800">
                    Không có lịch hẹn lại. Bấm{" "}
                    <button
                      type="button"
                      className="font-bold underline"
                      onClick={() => handleMobileTabChange("all")}
                    >
                      Nhật ký
                    </button>{" "}
                    để xem liên hệ đã ghi, hoặc tạo mới.
                  </div>
                ) : (
                  <motion.div
                    className="space-y-3"
                    variants={cardListVariants}
                    initial="hidden"
                    animate="show"
                  >
                    {contacts.map((item) =>
                      renderMobileContactCard(item, { emphasizeFollowup: true })
                    )}
                  </motion.div>
                )}
              </>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Nhật ký gần đây ({total})
                </p>
                {loading ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
                    Đang tải…
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
                    Chưa có liên hệ phù hợp bộ lọc.
                  </div>
                ) : (
                  <motion.div
                    className="space-y-3"
                    variants={cardListVariants}
                    initial="hidden"
                    animate="show"
                  >
                    {contacts.map((item) => renderMobileContactCard(item))}
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ===== DESKTOP: reminder + table ===== */}
      <div className="mb-6 hidden md:block" data-tour="contact-reminder-section">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-black uppercase tracking-wider text-amber-700">
            {followupFilterActive
              ? `1) Tất cả lịch hẹn (${reminderContacts.length}${
                  total > reminderContacts.length ? ` / ${total}` : ""
                })`
              : `1) Liên lạc cần xử lý hôm nay / quá hạn (${
                  reminderContacts.length
                }${
                  stats.actionable_followups > reminderContacts.length
                    ? ` / ${stats.actionable_followups}`
                    : ""
                })`}
          </h2>
          {followupFilterActive ? (
            <button
              type="button"
              onClick={() => {
                setStatFilter(null);
                setCurrentPage(1);
              }}
              className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold text-amber-800"
            >
              Đang xem tất cả lịch hẹn · Bỏ lọc
            </button>
          ) : stats.upcoming_followups > 0 ? (
            <button
              type="button"
              onClick={() => handleStatCardClick("upcoming_followups")}
              className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold text-amber-800"
            >
              Xem tất cả lịch hẹn ({stats.upcoming_followups})
            </button>
          ) : null}
        </div>
        {reminderContacts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/60 px-5 py-4 text-sm text-amber-700">
            {followupFilterActive
              ? "Không có lịch hẹn nào theo bộ lọc hiện tại."
              : "Không có lịch cần liên hệ hôm nay hoặc quá hạn."}
            {!followupFilterActive && stats.upcoming_followups > 0 ? (
              <button
                type="button"
                className="ml-2 font-bold underline"
                onClick={() => handleStatCardClick("upcoming_followups")}
              >
                Xem tất cả lịch hẹn ({stats.upcoming_followups})
              </button>
            ) : null}
            {followupFilterActive ? (
              <button
                type="button"
                className="ml-2 font-bold underline"
                onClick={() => {
                  setStatFilter(null);
                  setCurrentPage(1);
                }}
              >
                Bỏ lọc lịch hẹn
              </button>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {reminderContacts.map((item) => {
              const meta = getFollowupMeta(item.next_contact_date);
              return (
                <div
                  key={item.id}
                  className={`flex h-full flex-col rounded-2xl border bg-white p-4 shadow-sm ${
                    meta?.tone === "overdue"
                      ? "border-red-200"
                      : "border-amber-100"
                  }`}
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-gray-900">
                        {item.customer_name || "—"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatBranchAndGeoLine(item)}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full border px-3 py-1 text-[11px] font-bold ${
                        meta?.className || "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {meta?.label ||
                        `Hẹn: ${format(
                          new Date(item.next_contact_date),
                          "dd/MM/yyyy"
                        )}`}
                    </span>
                  </div>
                  <p className="min-h-[40px] line-clamp-2 text-sm text-gray-600">
                    {item.content || "—"}
                  </p>
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <div className="text-xs text-gray-500">
                      NV:{" "}
                      <span className="font-semibold text-gray-700">
                        {item.staff_name || "—"}
                      </span>{" "}
                      • {item.method || "—"}
                    </div>
                    <div className="flex items-center gap-2">
                      {canProgressItem(item) ? (
                        <button
                          type="button"
                          onClick={() => handleProgress(item.id, item)}
                          className="rounded-xl bg-red-700 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-red-800"
                          title="Cập nhật tiến độ"
                        >
                          Cập nhật tiến độ
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleView(item.id)}
                        className="rounded-xl p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-700"
                        title="Xem chi tiết"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!followupFilterActive ? (
      <div className="hidden md:block" data-tour="contact-completed-table">
        <h2 className="mb-3 text-sm font-black uppercase tracking-wider text-gray-700">
          2) Liên lạc đã hoàn thành ({completedContacts.length})
        </h2>
        <div className="mb-10 overflow-hidden rounded-[2rem] border border-gray-100 bg-white shadow-sm">
          <div className="max-h-[70vh] overflow-x-auto overflow-y-auto">
            <table className="w-max min-w-[1060px] text-sm">
              <colgroup>
                <col style={{ width: columnWidths.contact_date }} />
                <col style={{ width: columnWidths.customer_branch }} />
                <col style={{ width: columnWidths.staff_name }} />
                <col style={{ width: columnWidths.method }} />
                <col style={{ width: columnWidths.content_result }} />
              </colgroup>
              <thead className="bg-gray-50/95 backdrop-blur">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                  <th className="sticky top-0 z-10 relative border-b border-gray-100 bg-gray-50/95 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleSort("contact_date")}
                      className="flex items-center gap-1"
                    >
                      Ngày liên hệ <span>{sortMark("contact_date")}</span>
                    </button>
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveResize({
                          key: "contact_date",
                          startX: e.clientX,
                          startWidth: columnWidths.contact_date,
                        });
                      }}
                    />
                  </th>
                  <th className="sticky top-0 z-10 relative border-b border-gray-100 bg-gray-50/95 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleSort("customer_name")}
                      className="flex flex-col items-start gap-0.5 text-left"
                    >
                      <span className="flex items-center gap-1">
                        Khách hàng / Chi nhánh{" "}
                        <span>{sortMark("customer_name")}</span>
                      </span>
                    </button>
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveResize({
                          key: "customer_branch",
                          startX: e.clientX,
                          startWidth: columnWidths.customer_branch,
                        });
                      }}
                    />
                  </th>
                  <th className="sticky top-0 z-10 relative border-b border-gray-100 bg-gray-50/95 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleSort("staff_name")}
                      className="flex items-center gap-1"
                    >
                      Nhân viên <span>{sortMark("staff_name")}</span>
                    </button>
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveResize({
                          key: "staff_name",
                          startX: e.clientX,
                          startWidth: columnWidths.staff_name,
                        });
                      }}
                    />
                  </th>
                  <th className="sticky top-0 z-10 relative border-b border-gray-100 bg-gray-50/95 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleSort("method")}
                      className="flex items-center gap-1"
                    >
                      Hình thức <span>{sortMark("method")}</span>
                    </button>
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveResize({
                          key: "method",
                          startX: e.clientX,
                          startWidth: columnWidths.method,
                        });
                      }}
                    />
                  </th>
                  <th className="sticky top-0 z-10 relative border-b border-gray-100 bg-gray-50/95 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleSort("content")}
                      className="flex flex-col items-start gap-0.5 text-left"
                    >
                      <span className="flex items-center gap-1">
                        Nội dung / Kết quả <span>{sortMark("content")}</span>
                      </span>
                    </button>
                    <div
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveResize({
                          key: "content_result",
                          startX: e.clientX,
                          startWidth: columnWidths.content_result,
                        });
                      }}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {completedContacts.map((item) => (
                  <tr
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleView(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleView(item.id);
                      }
                    }}
                    className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-red-50/30"
                  >
                    <td className="px-4 py-3 font-semibold text-gray-700">
                      {item.contact_date
                        ? format(new Date(item.contact_date), "dd/MM/yyyy")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <p className="font-bold leading-snug text-gray-900">
                        {item.customer_name || "—"}
                      </p>
                      <p className="mt-0.5 text-xs leading-snug text-gray-500">
                        {[item.branch_name, item.customer_ward]
                          .filter(Boolean)
                          .join(" • ") || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.staff_name || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-[11px] font-bold text-gray-700">
                        {item.method || "—"}
                      </span>
                    </td>
                    <td className="max-w-[520px] px-4 py-3 align-top">
                      <p
                        className="line-clamp-3 text-sm leading-relaxed text-gray-800"
                        title={item.content || ""}
                      >
                        {item.content || "—"}
                      </p>
                      {item.result ? (
                        <p
                          className="mt-1.5 border-t border-gray-100 pt-1.5 text-xs font-semibold leading-snug text-emerald-800"
                          title={item.result}
                        >
                          Kết quả: {item.result}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {completedContacts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      Không có liên lạc đã hoàn thành phù hợp bộ lọc.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      ) : null}

      <div className="mt-4" data-tour="contact-pagination">
        <Pagination
          totalRecords={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>

      <CreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        editingContact={editingContact}
        mode={createMode}
        onRefresh={fetchContacts}
      />

      <DetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        contact={selectedContact}
        relatedLogs={relatedLogs}
        onProgress={
          selectedContact?.id && canProgressItem(selectedContact)
            ? () => handleProgress(selectedContact.id, selectedContact)
            : undefined
        }
      />
    </div>
  );
};

export default ContactHistoryListPage;
